// @ts-nocheck
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { CircularProgress, MenuItem } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import ControlledSelect from './ControlledSelect';

// Constants
const ALL_OPTION_DEFAULT_LABEL = 'All Items';
const ALL_OPTION_DEFAULT_VALUE = 0;
const SIZE_DEFAULT: 'small' | 'medium' = 'small';
const DISABLE_PORTAL_DEFAULT = false;
const SHOW_DETAILS_ICON_DEFAULT = true;
const DETAILS_ICON_DEFAULT_URL = '/Content/icons/kanban/jira-icons/release_details.svg';
const SELECTED_COLOR = '#4BAEE8';
const MARGIN_LEFT_SMALL = '8px';
const ICON_PADDING = '4px';
const LOADING_SPINNER_SIZE = 20;
// Breakpoint for hiding additional fields on smaller screens (matches design spec for responsive layout)
const WINDOW_WIDTH_BREAKPOINT = 1050;
const TAB_INDEX_DISABLED = -1;
const RESERVED_KEYS = ['value', 'label', 'originalData'] as const;

const SELECT_MENU_PAPER_PROPS = {
  PaperProps: {
    style: {
      maxHeight: 300,
      overflow: 'auto',
      zIndex: '6 !important',
    },
  },
};

// Helpers
const getAdditionalEntries = (option: Record<string, any>): [string, any][] =>
  Object.entries(option).filter(
    ([key]) => !RESERVED_KEYS.includes(key as (typeof RESERVED_KEYS)[number])
  );

const hasAdditional = (option: Record<string, any>): boolean =>
  getAdditionalEntries(option).length > 0;

const formatAdditionalValue = (val: any): string => (val === null ? 'now' : `${val}`);

const getAdditionalDisplay = (option: Record<string, any>): string =>
  `(${getAdditionalEntries(option)
    .map(([_, val]) => formatAdditionalValue(val))
    .join(' - ')})`;

// Helper function to derive focused icon URL from the base icon URL
const getFocusedIconUrl = (iconUrl: string): string => {
  // Simple strategy: replace the file extension with _focused.svg
  // This handles common patterns like:
  // - /path/to/icon.svg -> /path/to/icon_focused.svg
  // - /path/to/icon.png -> /path/to/icon_focused.svg
  return iconUrl.replace(/\.(svg|png|jpg|jpeg)$/, '_focused.svg');
};

// Helper function to render additional fields
const renderAdditionalFields = (
  option: Record<string, any>,
  windowWidth: number
): React.ReactNode => {
  if (!hasAdditional(option)) return null;

  if (windowWidth >= WINDOW_WIDTH_BREAKPOINT) {
    return <span style={{ marginLeft: MARGIN_LEFT_SMALL }}>{getAdditionalDisplay(option)}</span>;
  } else {
    return <span style={{ marginLeft: MARGIN_LEFT_SMALL }}>...</span>;
  }
};

// Helper function to render option content
const renderOptionContent = (
  option: Record<string, any>,
  currentValue: string | number | undefined,
  windowWidth: number,
  renderCustomContent?: (option: any, isSelected: boolean) => React.ReactNode
): React.ReactNode => {
  if (renderCustomContent) {
    return renderCustomContent(option, option.value === currentValue);
  }

  return (
    <>
      {option.label && <span>{option.label}</span>}
      {renderAdditionalFields(option, windowWidth)}
    </>
  );
};

export interface ISelectOption {
  value: string | number;
  label: string;
  [key: string]: any;
}

interface IUniversalSelectProps<T = any> {
  data: T[];

  getOptionValue: (item: T) => string | number;
  getOptionLabel: (item: T) => string;
  getAdditionalFields?: (item: T) => Record<string, any>;
  getOptionDisabled?: (item: T | 'all') => boolean; // nullable/optional

  onChange: (value: string | number) => void;
  onDetailsClick?: (item: T) => void;

  value?: string | number;
  defaultValue?: string | number;
  loading?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;

  includeAllOption?: boolean;
  allOptionLabel?: string;
  allOptionValue?: string | number;

  size?: 'small' | 'medium';
  disablePortal?: boolean;
  showDetailsIcon?: boolean;
  detailsIconUrl?: string;
  focusedIconUrl?: string;

  renderCustomContent?: (option: ISelectOption, isSelected: boolean) => React.ReactNode;
}

const UniversalSelect = <T,>({
  data,
  getOptionValue,
  getOptionLabel,
  getAdditionalFields = () => ({}),
  getOptionDisabled,
  onChange,
  onDetailsClick,
  value,
  defaultValue,
  loading = false,
  disabled = false,
  fullWidth = false,
  includeAllOption = true,
  allOptionLabel = ALL_OPTION_DEFAULT_LABEL,
  allOptionValue = ALL_OPTION_DEFAULT_VALUE,
  size = SIZE_DEFAULT,
  disablePortal = DISABLE_PORTAL_DEFAULT,
  showDetailsIcon = SHOW_DETAILS_ICON_DEFAULT,
  detailsIconUrl = DETAILS_ICON_DEFAULT_URL,
  focusedIconUrl, // New prop
  renderCustomContent,
}: IUniversalSelectProps<T>) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Memoize the focused icon URL calculation
  const computedFocusedIconUrl = useMemo(() => {
    // Use provided focusedIconUrl if available, otherwise derive it from detailsIconUrl
    return focusedIconUrl || getFocusedIconUrl(detailsIconUrl);
  }, [focusedIconUrl, detailsIconUrl]);

  const transformItemToOption = useCallback(
    (item: T | 'all'): ISelectOption => {
      if (item === 'all') {
        return {
          value: allOptionValue,
          label: allOptionLabel,
        };
      }

      return {
        value: getOptionValue(item),
        label: getOptionLabel(item),
        ...getAdditionalFields(item),
        originalData: item,
      };
    },
    [getOptionValue, getOptionLabel, getAdditionalFields, allOptionValue, allOptionLabel]
  );

  const currentValue = value !== undefined ? value : defaultValue;

  const selectedItem = useMemo(() => {
    if (currentValue === undefined) return undefined;
    if (currentValue === allOptionValue) return 'all';
    return data.find((item) => getOptionValue(item) === currentValue);
  }, [currentValue, data, getOptionValue, allOptionValue]);

  const selectedOption = useMemo(() => {
    if (!selectedItem) return undefined;
    return transformItemToOption(selectedItem);
  }, [selectedItem, transformItemToOption]);

  const handleChange = (event: SelectChangeEvent<string | number>) => {
    onChange(event.target.value);
  };

  const handleDetailsClick = (e: React.MouseEvent, item: T) => {
    e.stopPropagation();
    if (onDetailsClick) {
      onDetailsClick(item);
    }
  };

  const optionsMemo = useMemo(() => {
    const options: React.ReactNode[] = [];

    // Add "All" option if needed
    if (includeAllOption) {
      const allOption = transformItemToOption('all');

      options.push(
        <MenuItem
          value={allOption.value}
          key={`${allOption.value}-${allOption.label}`}
          disabled={getOptionDisabled ? getOptionDisabled('all') : false}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            '&.Mui-selected': {
              color: SELECTED_COLOR,
            },
          }}
        >
          <div style={{ marginLeft: MARGIN_LEFT_SMALL, flex: 1 }}>
            {renderOptionContent(allOption, currentValue, windowWidth, renderCustomContent)}
          </div>
        </MenuItem>
      );
    }

    // Add data items
    data.forEach((item) => {
      const option = transformItemToOption(item);

      options.push(
        <MenuItem
          value={option.value}
          key={`${option.value}-${option.label}`}
          disabled={getOptionDisabled ? getOptionDisabled(item) : false}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            '&.Mui-selected': {
              color: SELECTED_COLOR,
            },
            '&.Mui-selected #icon': {
              content: showDetailsIcon ? `url("${computedFocusedIconUrl}")` : 'none',
            },
          }}
        >
          <div style={{ marginLeft: MARGIN_LEFT_SMALL, flex: 1 }}>
            {renderOptionContent(option, currentValue, windowWidth, renderCustomContent)}
          </div>

          {isDropdownOpen && showDetailsIcon && onDetailsClick && (
            <button
              type="button"
              title="Details"
              aria-label="Show details"
              onClick={(e) => handleDetailsClick(e, item)}
              style={{
                cursor: 'pointer',
                padding: ICON_PADDING,
                background: 'none',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <img src={detailsIconUrl} id="icon" alt="View release details" />
            </button>
          )}
        </MenuItem>
      );
    });

    return options;
  }, [
    data,
    includeAllOption,
    allOptionValue,
    allOptionLabel,
    currentValue,
    isDropdownOpen,
    windowWidth,
    renderCustomContent,
    showDetailsIcon,
    onDetailsClick,
    detailsIconUrl,
    computedFocusedIconUrl,
    transformItemToOption,
    getOptionDisabled,
  ]);

  const renderOptions = () => optionsMemo;

  const renderValue = (selected: string | number) => {
    if (loading) {
      return (
        <div className="loading" style={{ display: 'flex', alignItems: 'center' }}>
          <CircularProgress
            size={LOADING_SPINNER_SIZE}
            style={{ marginRight: MARGIN_LEFT_SMALL }}
          />
          Loading...
        </div>
      );
    }

    if (renderCustomContent && selectedOption) {
      return renderCustomContent(selectedOption, true);
    }

    return (
      <div style={{ marginLeft: MARGIN_LEFT_SMALL }}>
        {selectedOption && (
          <>
            {selectedOption.label && <span>{selectedOption.label}</span>}
            {renderAdditionalFields(selectedOption, windowWidth)}
          </>
        )}
      </div>
    );
  };

  // Ensure the Select component is always controlled
  const selectValue =
    currentValue !== undefined ? currentValue : includeAllOption ? allOptionValue : '';

  return (
    <ControlledSelect
      size={size}
      fullWidth={fullWidth}
      value={selectValue}
      onChange={handleChange}
      onOpen={() => setIsDropdownOpen(true)}
      onClose={() => setIsDropdownOpen(false)}
      disabled={disabled || loading}
      tabIndex={TAB_INDEX_DISABLED}
      MenuProps={{
        disablePortal: disablePortal,
        ...SELECT_MENU_PAPER_PROPS,
      }}
      renderValue={renderValue}
    >
      {renderOptions()}
    </ControlledSelect>
  );
};

export default UniversalSelect;
